import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import usePermissions from '../../../hooks/usePermissions';
import {
  Add, Edit, Visibility, Delete, Payment, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, Print, Search as SearchIcon
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Typography, Box, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Fade,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import PaymentReceiptsModal from './PaymentReceiptsModal';
import Pagination from '../../common/Pagination';
import './PaymentReceiptsIndex.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const PaymentReceiptsIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId } = useAuth();
  const { canCreate, canEdit, canDelete } = usePermissions('Accounts');

  // Remove old stale permission checks — replaced by usePermissions above

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [dropdownData, setDropdownData] = useState({ Master: [], AccountHead: [], Bank: [] });
  const [banks, setBanks] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // payment or receipt
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mounted, setMounted] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states - initialData is passed to modal; modal owns its own state
  const [initialData, setInitialData] = useState(null);
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Cheque status update modal
  const [chequeStatusModalOpen, setChequeStatusModalOpen] = useState(false);
  const [selectedChequeItem, setSelectedChequeItem] = useState(null);
  const [newChequeStatus, setNewChequeStatus] = useState(1);
  const [bulkChequeStatusModalOpen, setBulkChequeStatusModalOpen] = useState(false);
  const [selectedChequeItems, setSelectedChequeItems] = useState([]);
  const [individualChequeStatuses, setIndividualChequeStatuses] = useState({}); // Store individual status for each item

  // Drill-down states for animated expansion - REMOVED (not needed)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (companyId && token) {
      fetchDropdownData();
      fetchPaymentMethods();
      fetchCompanyData();
    }
  }, [companyId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchPaymentReceipts();
    }
  }, [companyId, token, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch functions
  const fetchPaymentReceipts = async () => {
    try {
      const activeResponse = await fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PaymentAndReceiptApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive payment receipts:', inactiveError);
      }

      setPaymentReceipts([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching payment receipts:', error);
      setPaymentReceipts([]);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [mastersRes, accountHeadsRes, banksRes] = await Promise.allSettled([
        fetchData('MasterApi', `/${companyId}`, token),
        fetchData('AccHeadApi', `/${companyId}`, token),
        fetchData('BankApi', `/${companyId}`, token),
      ]);
      const mastersList = mastersRes.status === 'fulfilled' ? (mastersRes.value.response_data || mastersRes.value || []) : [];
      const accountHeadsList = accountHeadsRes.status === 'fulfilled' ? (accountHeadsRes.value.response_data || accountHeadsRes.value || []) : [];
      const banksList = banksRes.status === 'fulfilled' ? (banksRes.value.response_data || banksRes.value || []) : [];
      setDropdownData({ Master: mastersList, AccountHead: accountHeadsList, Bank: banksList });
      setBanks(banksList);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token);
      const data = response.response_data || response || [];
      setPaymentMethods(data.length > 0 ? data : [
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true },
      ]);
    } catch {
      setPaymentMethods([
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true },
      ]);
    }
  };

  const fetchGstRates = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/GetGSTList/${companyId}`, token);
      setGstRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching GST rates:', error);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(response.response_data || response || null);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyData(null);
    }
  };

  const fetchMaxBillNo = useCallback(async (isType = true) => {
    try {
      const isTypeString = isType.toString();
      const response = await fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId || 1}/${companyId}/${isTypeString}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  }, [branchId, companyId, token]);

  // Fetch specific payment receipt details by ID
  const fetchPaymentReceiptDetails = async (paymentReceiptId) => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentReceiptId}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching payment receipt details:', error);
      // Try alternative endpoint if GetById doesn't work
      try {
        const altResponse = await fetchData('PaymentAndReceiptApi', `/${paymentReceiptId}`, token);
        return altResponse.response_data || altResponse || null;
      } catch (altError) {
        console.error('Error with alternative endpoint:', altError);
        return null;
      }
    }
  };

  // Print Payment/Receipt handler - Professional jsPDF Format
  const handlePrintPaymentReceipt = useCallback(async (paymentReceiptId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');

    try {
      const freshData = await fetchPaymentReceiptDetails(paymentReceiptId);

      if (!freshData) {
        setError('Failed to fetch payment receipt details for printing.');
        setLoading(false);
        return;
      }

      const paymentData = freshData.PaymentAndReceipt || freshData;
      const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
      const saleDetails = freshData.PaymentAndReceiptSale || [];

      // Get master details
      const master = dropdownData.Master.find(m => m.Id === paymentData.MasterId);
      const masterName = paymentData.MasterName || master?.Name || '-';
      const masterAddress = master?.Address || '';
      const masterGST = master?.GSTNumber || '';
      const masterContact = master?.ContactNumber || master?.Mobile || '';

      // Get bank details if applicable
      const bank = banks.find(b => b.Id === paymentData.BankId);
      const bankName = bank?.Name || '';

      // Get payment method
      const paymentMethod = paymentMethods.find(pm => pm.Id === paymentData.PaymentType);
      const paymentMethodName = paymentMethod?.Name || 'Cash';

      // Company details
      const companyNameDisplay = companyData?.Name || 'Company Name';
      const companyAddress = companyData?.Address || '';
      const companyGST = companyData?.GSTNumber || '';
      const companyContact = companyData?.ContactNumber || '';
      const companyEmail = companyData?.Email || '';

      const isPayment = paymentData.IsType;
      const documentType = isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER';
      const partyLabel = isPayment ? 'Paid To' : 'Received From';

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 15;
      let yPosition = margin;

      // Enhanced color scheme
      const primaryColor = isPayment ? [220, 38, 127] : [16, 185, 129]; // Pink for Payment, Green for Receipt
      const secondaryColor = isPayment ? [190, 24, 93] : [5, 150, 105];
      const accentColor = [59, 130, 246]; // Blue accent
      const textColor = [17, 24, 39];
      const lightGray = [248, 250, 252];
      const mediumGray = [148, 163, 184];
      const darkGray = [71, 85, 105];

      // Helper function to add styled text
      const addStyledText = (text, x, y, options = {}) => {
        const {
          fontSize = 10,
          fontStyle = 'normal',
          color = textColor,
          align = 'left',
          maxWidth = null
        } = options;

        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);

        if (maxWidth) {
          const lines = pdf.splitTextToSize(text, maxWidth);
          pdf.text(lines, x, y, { align });
          return y + (lines.length * fontSize * 0.35);
        } else {
          pdf.text(text, x, y, { align });
          return y + (fontSize * 0.35);
        }
      };

      // Helper function to add rectangle with border
      const addStyledRect = (x, y, width, height, options = {}) => {
        const {
          fillColor = null,
          borderColor = [0, 0, 0],
          borderWidth = 0.5
        } = options;

        if (fillColor) {
          pdf.setFillColor(...fillColor);
          pdf.rect(x, y, width, height, 'F');
        }

        pdf.setDrawColor(...borderColor);
        pdf.setLineWidth(borderWidth);
        pdf.rect(x, y, width, height, 'S');
      };

      // Company Header with styling
      addStyledRect(margin, yPosition, pageWidth - 2 * margin, 25, {
        fillColor: lightGray,
        borderColor: primaryColor,
        borderWidth: 1
      });

      yPosition += 5;
      yPosition = addStyledText(companyNameDisplay, pageWidth / 2, yPosition, {
        fontSize: 16,
        fontStyle: 'bold',
        color: primaryColor,
        align: 'center'
      });

      if (companyAddress) {
        yPosition = addStyledText(companyAddress, pageWidth / 2, yPosition + 3, {
          fontSize: 9,
          align: 'center',
          maxWidth: pageWidth - 2 * margin - 10
        });
      }

      const contactInfo = [
        companyGST ? `GST: ${companyGST}` : '',
        companyContact ? `Tel: ${companyContact}` : '',
        companyEmail ? `Email: ${companyEmail}` : ''
      ].filter(Boolean).join(' | ');

      if (contactInfo) {
        yPosition = addStyledText(contactInfo, pageWidth / 2, yPosition + 2, {
          fontSize: 8,
          align: 'center'
        });
      }

      yPosition += 10;

      // Document Title with colored background
      addStyledRect(margin, yPosition, pageWidth - 2 * margin, 12, {
        fillColor: primaryColor,
        borderColor: primaryColor,
        borderWidth: 1
      });

      yPosition = addStyledText(documentType, pageWidth / 2, yPosition + 8, {
        fontSize: 14,
        fontStyle: 'bold',
        color: [255, 255, 255],
        align: 'center'
      });

      yPosition += 8;

      // Voucher Information Section
      const leftColWidth = (pageWidth - 2 * margin) * 0.65;
      const rightColWidth = (pageWidth - 2 * margin) * 0.35;

      // Party Information Box
      addStyledRect(margin, yPosition, leftColWidth, 35, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let partyYPos = yPosition + 5;
      partyYPos = addStyledText(partyLabel, margin + 5, partyYPos, {
        fontSize: 10,
        fontStyle: 'bold',
        color: primaryColor
      });

      partyYPos = addStyledText(masterName, margin + 15, partyYPos + 3, {
        fontSize: 11,
        fontStyle: 'bold'
      });

      if (masterAddress) {
        partyYPos = addStyledText(masterAddress, margin + 15, partyYPos + 2, {
          fontSize: 9,
          maxWidth: leftColWidth - 25
        });
      }

      if (masterGST) {
        partyYPos = addStyledText(`GST: ${masterGST}`, margin + 15, partyYPos + 2, {
          fontSize: 9
        });
      }

      if (masterContact) {
        addStyledText(`Contact: ${masterContact}`, margin + 15, partyYPos + 2, {
          fontSize: 9
        });
      }

      // Voucher Details Box
      addStyledRect(margin + leftColWidth, yPosition, rightColWidth, 35, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let detailsYPos = yPosition + 5;
      const detailsX = margin + leftColWidth + 5;

      detailsYPos = addStyledText(`Voucher No: ${paymentData.BillNumber}`, detailsX, detailsYPos, {
        fontSize: 9,
        fontStyle: 'bold'
      });

      detailsYPos = addStyledText(`Date: ${formatDate(paymentData.BillDate)}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      detailsYPos = addStyledText(`FY: ${paymentData.FinYear || new Date().getFullYear()}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      detailsYPos = addStyledText(`Mode: ${paymentMethodName}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      if (paymentData.ChequeNumber) {
        detailsYPos = addStyledText(`Ref: ${paymentData.ChequeNumber}`, detailsX, detailsYPos + 3, {
          fontSize: 9
        });
      }

      if (bankName) {
        addStyledText(`Bank: ${bankName}`, detailsX, detailsYPos + 3, {
          fontSize: 9
        });
      }

      yPosition += 45;

      // Transaction Details Table
      const tableData = [];
      let totalAmount = parseFloat(paymentData.Amount) || 0;

      if (isPayment && purchaseDetails.length > 0) {
        purchaseDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Purchase Bill Payment',
            `Purchase ID: ${d.PurchaseId || '-'}`,
            formatCurrency(d.Amount || 0)
          ]);
        });
      } else if (!isPayment && saleDetails.length > 0) {
        saleDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Sale Bill Receipt',
            `Sale ID: ${d.SaleId || '-'}`,
            formatCurrency(d.Amount || 0)
          ]);
        });
      } else {
        tableData.push([
          '1',
          isPayment ? 'General Payment' : 'General Receipt',
          paymentData.Remarks || `Direct ${isPayment ? 'Payment' : 'Receipt'}`,
          formatCurrency(totalAmount)
        ]);
      }

      // Create table with autoTable
      if (typeof pdf.autoTable === 'function') {
        pdf.autoTable({
          startY: yPosition,
          head: [['Sr.No', 'Particulars', 'Description', 'Amount']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontSize: 11,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 4
          },
          bodyStyles: {
            fontSize: 10,
            textColor: textColor,
            cellPadding: 3
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 20 },
            1: { halign: 'left', cellWidth: 50 },
            2: { halign: 'left', cellWidth: 75 },
            3: { halign: 'right', cellWidth: 30, fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          tableLineColor: primaryColor,
          tableLineWidth: 0.8,
          alternateRowStyles: {
            fillColor: [248, 250, 252]
          }
        });

        yPosition = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + 10 : yPosition + 100;
      } else {
        console.error('autoTable plugin not loaded properly');
        yPosition += 100; // Fallback positioning
      }


      // Amount Summary Section
      const summaryBoxHeight = 40;
      const leftSummaryWidth = (pageWidth - 2 * margin) * 0.6;
      const rightSummaryWidth = (pageWidth - 2 * margin) * 0.4;

      // Amount in Words Box
      addStyledRect(margin, yPosition, leftSummaryWidth, summaryBoxHeight, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let wordsYPos = yPosition + 5;
      wordsYPos = addStyledText('Amount in Words:', margin + 5, wordsYPos, {
        fontSize: 10,
        fontStyle: 'bold',
        color: primaryColor
      });

      wordsYPos = addStyledText(`Rupees ${numberToWords(totalAmount)} Only`, margin + 5, wordsYPos + 3, {
        fontSize: 9,
        fontStyle: 'italic',
        maxWidth: leftSummaryWidth - 10
      });

      if (paymentData.Remarks) {
        wordsYPos = addStyledText('Remarks:', margin + 5, wordsYPos + 5, {
          fontSize: 9,
          fontStyle: 'bold'
        });
        addStyledText(paymentData.Remarks, margin + 5, wordsYPos + 2, {
          fontSize: 8,
          maxWidth: leftSummaryWidth - 10
        });
      }

      // Amount Breakdown Box
      addStyledRect(margin + leftSummaryWidth, yPosition, rightSummaryWidth, summaryBoxHeight, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let amountYPos = yPosition + 5;
      const amountX = margin + leftSummaryWidth + 5;

      // Amount breakdown table
      const amountData = [
        ['Basic Amount:', formatCurrency(paymentData.Amount || 0)]
      ];

      if (paymentData.CGSTAmount > 0) {
        amountData.push(['CGST:', formatCurrency(paymentData.CGSTAmount)]);
      }
      if (paymentData.SGSTAmount > 0) {
        amountData.push(['SGST:', formatCurrency(paymentData.SGSTAmount)]);
      }
      if (paymentData.IGSTAmount > 0) {
        amountData.push(['IGST:', formatCurrency(paymentData.IGSTAmount)]);
      }
      if (paymentData.RoundOff !== 0) {
        amountData.push(['Round Off:', formatCurrency(paymentData.RoundOff || 0)]);
      }

      amountData.forEach(([label, amount]) => {
        addStyledText(label, amountX, amountYPos, { fontSize: 9 });
        addStyledText(amount, amountX + rightSummaryWidth - 15, amountYPos, {
          fontSize: 9,
          align: 'right'
        });
        amountYPos += 4;
      });

      // Total Amount with highlight
      addStyledRect(amountX - 2, amountYPos - 1, rightSummaryWidth - 6, 6, {
        fillColor: primaryColor
      });

      addStyledText('Total Amount:', amountX, amountYPos + 2, {
        fontSize: 10,
        fontStyle: 'bold',
        color: [255, 255, 255]
      });
      addStyledText(formatCurrency(paymentData.TotalAmount || totalAmount), amountX + rightSummaryWidth - 15, amountYPos + 2, {
        fontSize: 10,
        fontStyle: 'bold',
        color: [255, 255, 255],
        align: 'right'
      });

      yPosition += summaryBoxHeight + 15;

      // Signature Section
      const signatureHeight = 25;
      const signatureWidth = (pageWidth - 2 * margin) / 3;

      ['PREPARED BY', 'CHECKED BY', 'AUTHORIZED SIGNATORY'].forEach((title, index) => {
        const x = margin + (index * signatureWidth);
        addStyledRect(x, yPosition, signatureWidth, signatureHeight, {
          fillColor: [255, 255, 255],
          borderColor: primaryColor,
          borderWidth: 0.8
        });

        addStyledText(title, x + signatureWidth / 2, yPosition + signatureHeight - 3, {
          fontSize: 9,
          fontStyle: 'bold',
          align: 'center'
        });
      });

      yPosition += signatureHeight + 10;

      // Footer
      addStyledText('This is a computer generated document and does not require signature.', pageWidth / 2, yPosition, {
        fontSize: 8,
        color: darkGray,
        align: 'center'
      });

      // Add watermark
      pdf.setTextColor(200, 200, 200);
      pdf.setFontSize(60);
      pdf.text(isPayment ? 'PAYMENT' : 'RECEIPT', pageWidth / 2, pageHeight / 2, {
        align: 'center',
        angle: 45
      });

      // Direct Print instead of download
      pdf.autoPrint();

      // Open in new window for printing
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // Clean up the URL after printing
          setTimeout(() => {
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        };
      } else {
        // Fallback if popup is blocked
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${documentType.replace(' ', '_')}_${paymentData.BillNumber}_${formatDate(paymentData.BillDate).replace(/\//g, '-')}.pdf`;
        link.click();
        URL.revokeObjectURL(pdfUrl);
      }

      setSuccess(`Document prepared for printing: ${documentType} ${paymentData.BillNumber}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF.');
    }

    setLoading(false);
  }, [companyId, branchId, token, companyData]);

  // Helper function to convert number to words (basic implementation)
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    let result = '';
    const crores = Math.floor(num / 10000000);
    if (crores > 0) {
      result += convertHundreds(crores) + 'Crore ';
      num %= 10000000;
    }

    const lakhs = Math.floor(num / 100000);
    if (lakhs > 0) {
      result += convertHundreds(lakhs) + 'Lakh ';
      num %= 100000;
    }

    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      result += convertHundreds(thousands) + 'Thousand ';
      num %= 1000;
    }

    if (num > 0) {
      result += convertHundreds(num);
    }

    return result.trim();
  };

  // Handle modal actions
  const handleOpenModal = useCallback(async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);

    if (action === 'create') {
      setInitialData(null);
      setPaymentReceiptDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${item.Id}`, token);
        const paymentReceiptData = response.response_data || response;

        // Handle both wrapped { PaymentAndReceipt: {...} } and flat response shapes
        const paymentData = paymentReceiptData.PaymentAndReceipt || paymentReceiptData;

        if (!paymentData || !paymentData.Id) {
          throw new Error('Invalid response structure');
        }

        setInitialData({
          ...paymentData,
          BillDate: paymentData.BillDate ? paymentData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          CheqDate: paymentData.CheqDate ? paymentData.CheqDate.split('T')[0] : '',
          AcType: paymentData.AcType || 1,
        });

        // Set payment receipt details from the API response
        const purchaseDetails = paymentReceiptData.PaymentAndReceiptPurchase || [];
        const saleDetails = paymentReceiptData.PaymentAndReceiptSale || [];

        if (paymentData.IsTypeReceipt === false && purchaseDetails.length > 0) {
          // Payment - show purchase details
          const mappedDetails = purchaseDetails.map(p => ({
            Id: p.PurchaseId,
            Description: p.PurchaseDescription || `Purchase - ${p.PurchaseId}`,
            Amount: p.PurchaseAmount || 0,
            PaidAmount: p.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: p.BillDate || paymentData.BillDate,
            Type: 'Purchase',
            PaymentAmount: p.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else if (paymentData.IsTypeReceipt === true && saleDetails.length > 0) {
          // Receipt - show sale details
          const mappedDetails = saleDetails.map(s => ({
            Id: s.SaleId,
            Description: s.SaleDescription || `Sale - ${s.SaleId}`,
            Amount: s.SaleAmount || 0,
            PaidAmount: s.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: s.BillDate || paymentData.BillDate,
            Type: 'Sale',
            PaymentAmount: s.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else {
          // No specific purchase/sale details - create a general entry
          setPaymentReceiptDetails([{
            Id: paymentData.Id,
            Description: `${paymentData.IsTypeReceipt ? 'Receipt' : 'Payment'} - ${paymentData.BillNo}`,
            Amount: paymentData.Amount,
            PaidAmount: paymentData.Amount,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: paymentData.BillDate,
            Type: paymentData.IsTypeReceipt ? 'Receipt' : 'Payment',
            PaymentAmount: paymentData.Amount,
            IsSelected: true,
            isDirectPayment: true
          }]);
        }

      } catch (error) {
        console.error('Error fetching payment receipt details:', error);
        // Do NOT show error toast — fall back silently using list item data
        setInitialData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          CheqDate: item.CheqDate ? item.CheqDate.split('T')[0] : '',
          AcType: item.AcType || 1,
        });

        // Create a basic entry for fallback
        setPaymentReceiptDetails([{
          Id: item.Id,
          Description: `${item.IsTypeReceipt ? 'Receipt' : 'Payment'} - ${item.BillNo}`,
          Amount: item.Amount,
          PaidAmount: item.Amount,
          BalanceAmount: 0,
          PendingAmount: 0,
          BillDate: item.BillDate,
          Type: item.IsTypeReceipt ? 'Receipt' : 'Payment',
          PaymentAmount: item.Amount,
          IsSelected: true,
          isDirectPayment: true
        }]);
      }
      setModalLoading(false);
    }

    setOpenModal(true);
  }, [companyId, branchId, token, userId, fetchMaxBillNo]);

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    setSelectedItem(null);
    setInitialData(null);
    setPaymentReceiptDetails([]);
  }, []);

  // Handle save — receives formData from modal
  const handleSave = useCallback(async (formData) => {
    setModalLoading(true);
    try {
      const dataToSend = {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          Companyid: companyId,
          FinYear: formData.FinYear || '',
          BillNo: parseInt(formData.BillNo) || 0,
          BillDate: formData.BillDate,
          AcType: formData.AcType || 1,
          MasterId: formData.MasterId || null,
          AccountHeadId: formData.AccountHeadId || null,
          BankId: formData.BankId || null,
          Amount: parseFloat(formData.Amount) || 0,
          IsCancelled: formData.IsCancelled || false,
          IsTypeReceipt: formData.IsTypeReceipt || false,
          PaymentMode: formData.PaymentMode || 1,
          ChequeNo: formData.ChequeNo || '',
          CheqDate: formData.CheqDate || '0001-01-01',
          CheqBankId: formData.CheqBankId || null,
          Remarks: formData.Remarks || null,
          CreatedBy: modalAction === 'create' ? userId : (formData.CreatedBy || userId),
          CreatedDate: modalAction === 'create' ? new Date().toISOString().split('T')[0] : (formData.CreatedDate || new Date().toISOString().split('T')[0]),
          ModifiedBy: modalAction === 'edit' ? userId : null,
          ModifiedDate: modalAction === 'edit' ? new Date().toISOString().split('T')[0] : null,
      };


      // No purchase/sale detail lines needed with new model

      if (modalAction === 'create') {
        await postData('PaymentAndReceiptApi','', dataToSend, token);
        setSuccess(`${formData.IsTypeReceipt ? 'Receipt' : 'Payment'} created successfully!`);
      } else {
        await updateData('PaymentAndReceiptApi', formData.Id, dataToSend, token);
        setSuccess(`${formData.IsTypeReceipt ? 'Receipt' : 'Payment'} updated successfully!`);
      }

      handleCloseModal();
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error saving payment receipt:', error);
      setError(`Failed to save ${formData.IsTypeReceipt ? 'receipt' : 'payment'}.`);
    }
    setModalLoading(false);
  }, [companyId, branchId, userId, modalAction, handleCloseModal, token]);

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteData('PaymentAndReceiptApi', deleteItemId, token);
      setSuccess('Payment/Receipt deleted successfully!');
      setDeleteModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error deleting payment receipt:', error);
      setError('Failed to delete payment receipt.');
    }
  };

  // Handle activate
  const handleActivate = async () => {
    try {
      await activateData('PaymentAndReceiptApi', activateItemId, token);
      setSuccess('Payment/Receipt activated successfully!');
      setActivateModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error activating payment receipt:', error);
      setError('Failed to activate payment receipt.');
    }
  };

  // Wrapper functions for new UI
  const handleDeleteConfirm = async () => {
    await handleDelete();
  };

  const handleActivateConfirm = async () => {
    await handleActivate();
  };

  const handleSavePaymentReceipt = async (formData) => {
    await handleSave(formData);
  };

  // Handle cheque status update
  const handleUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque status...');

      const updateData = {
        PaymentAndReceipt: {
          Id: selectedChequeItem.Id,
          Companyid: companyId,
          BranchId: branchId,
          MasterId: selectedChequeItem.MasterId,
          SubHeadId: selectedChequeItem.SubHeadId || 0,
          PartyId: selectedChequeItem.PartyId || selectedChequeItem.MasterId,
          BillDate: selectedChequeItem.BillDate,
          BillNumber: selectedChequeItem.BillNumber,
          FinYear: selectedChequeItem.FinYear || new Date().getFullYear().toString(),
          IsType: selectedChequeItem.IsType,
          IsGST: selectedChequeItem.IsGST || false,
          GSTHeadId: selectedChequeItem.GSTHeadId || 0,
          IsSaleAndPurchase: selectedChequeItem.IsSaleAndPurchase || true,
          PaymentType: selectedChequeItem.PaymentType,
          ChequeDate: selectedChequeItem.ChequeDate,
          ChequeNumber: selectedChequeItem.ChequeNumber,
          BankId: selectedChequeItem.BankId,
          chequeDetails: selectedChequeItem.chequeDetails,
          ChequeStatusId: newChequeStatus, // This is the key update
          Amount: selectedChequeItem.Amount,
          GSTRate: selectedChequeItem.GSTRate || 0,
          CGSTAmount: selectedChequeItem.CGSTAmount || 0,
          SGSTAmount: selectedChequeItem.SGSTAmount || 0,
          IGSTAmount: selectedChequeItem.IGSTAmount || 0,
          RoundOff: selectedChequeItem.RoundOff || 0,
          TotalAmount: selectedChequeItem.TotalAmount,
          Remarks: selectedChequeItem.Remarks,
          IsActive: selectedChequeItem.IsActive,
          CreatedBy: selectedChequeItem.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: selectedChequeItem.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', selectedChequeItem.Id, updateData, token);

      const statusText = newChequeStatus === 1 ? 'Pending' :
        newChequeStatus === 2 ? 'Passed' :
          newChequeStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" successfully!`);
      setChequeStatusModalOpen(false);
      setSelectedChequeItem(null);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque status:', error);
      setError('Failed to update cheque status.');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk cheque status update with individual statuses
  const handleBulkUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque statuses...');

      const updatePromises = selectedChequeItems.map(async (item) => {
        const individualStatus = individualChequeStatuses[item.Id] || newChequeStatus;

        const updatePayload = {
          PaymentAndReceipt: {
            Id: item.Id,
            Companyid: companyId,
            BranchId: branchId,
            MasterId: item.MasterId,
            SubHeadId: item.SubHeadId || 0,
            PartyId: item.PartyId || item.MasterId,
            BillDate: item.BillDate,
            BillNumber: item.BillNumber,
            FinYear: item.FinYear || new Date().getFullYear().toString(),
            IsType: item.IsType,
            IsGST: item.IsGST || false,
            GSTHeadId: item.GSTHeadId || 0,
            IsSaleAndPurchase: item.IsSaleAndPurchase || true,
            PaymentType: item.PaymentType,
            ChequeDate: item.ChequeDate,
            ChequeNumber: item.ChequeNumber,
            BankId: item.BankId,
            chequeDetails: item.chequeDetails,
            ChequeStatusId: individualStatus, // Use individual status for each item
            Amount: item.Amount,
            GSTRate: item.GSTRate || 0,
            CGSTAmount: item.CGSTAmount || 0,
            SGSTAmount: item.SGSTAmount || 0,
            IGSTAmount: item.IGSTAmount || 0,
            RoundOff: item.RoundOff || 0,
            TotalAmount: item.TotalAmount,
            Remarks: item.Remarks,
            IsActive: item.IsActive,
            CreatedBy: item.CreatedBy,
            UpdatedBy: userId,
            CreatedDate: item.CreatedDate,
            UpdatedDate: new Date().toISOString()
          }
        };

        return updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);
      });

      await Promise.all(updatePromises);

      setSuccess(`${selectedChequeItems.length} cheque statuses updated successfully!`);
      setBulkChequeStatusModalOpen(false);
      setSelectedChequeItems([]);
      setIndividualChequeStatuses({});
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque statuses:', error);
      setError('Failed to update cheque statuses.');
    } finally {
      setLoading(false);
    }
  };

  // Open cheque status update modal
  const handleOpenChequeStatusModal = (item) => {
    setSelectedChequeItem(item);
    setNewChequeStatus(item.ChequeStatusId || 1);
    setChequeStatusModalOpen(true);
  };

  // Get cheque payment method ID dynamically
  const getChequePaymentMethodId = () => {
    console.log('=== GETTING CHEQUE PAYMENT METHOD ID ===');
    console.log('Available payment methods:', paymentMethods);

    const chequeMethod = paymentMethods.find(pm =>
      pm.Name && pm.Name.toLowerCase().includes('cheque')
    );

    console.log('Found cheque method:', chequeMethod);

    const chequeId = chequeMethod ? chequeMethod.Id : 2;
    console.log('Using cheque payment method ID:', chequeId);
    console.log('=== END CHEQUE METHOD DEBUG ===');

    return chequeId;
  };

  // Handle individual cheque status update (single item)
  const handleIndividualChequeStatusUpdate = async (item, newStatus) => {
    try {
      setLoading(true);
      setLoadingMessage(`Updating cheque status for ${item.BillNumber}...`);

      const updatePayload = {
        PaymentAndReceipt: {
          Id: item.Id,
          Companyid: companyId,
          BranchId: branchId,
          MasterId: item.MasterId,
          SubHeadId: item.SubHeadId || 0,
          PartyId: item.PartyId || item.MasterId,
          BillDate: item.BillDate,
          BillNumber: item.BillNumber,
          FinYear: item.FinYear || new Date().getFullYear().toString(),
          IsType: item.IsType,
          IsGST: item.IsGST || false,
          GSTHeadId: item.GSTHeadId || 0,
          IsSaleAndPurchase: item.IsSaleAndPurchase || true,
          PaymentType: item.PaymentType,
          ChequeDate: item.ChequeDate,
          ChequeNumber: item.ChequeNumber,
          BankId: item.BankId,
          chequeDetails: item.chequeDetails,
          ChequeStatusId: newStatus,
          Amount: item.Amount,
          GSTRate: item.GSTRate || 0,
          CGSTAmount: item.CGSTAmount || 0,
          SGSTAmount: item.SGSTAmount || 0,
          IGSTAmount: item.IGSTAmount || 0,
          RoundOff: item.RoundOff || 0,
          TotalAmount: item.TotalAmount,
          Remarks: item.Remarks,
          IsActive: item.IsActive,
          CreatedBy: item.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: item.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);

      const statusText = newStatus === 1 ? 'Pending' :
        newStatus === 2 ? 'Passed' :
          newStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" for ${item.BillNumber}!`);

      // Update the individual status in state
      setIndividualChequeStatuses(prev => ({
        ...prev,
        [item.Id]: newStatus
      }));

      // Refresh the main data
      await fetchPaymentReceipts();

    } catch (error) {
      console.error('Error updating individual cheque status:', error);
      setError(`Failed to update cheque status for ${item.BillNumber}.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual cheque status change
  const handleIndividualStatusChange = (itemId, newStatus) => {
    setIndividualChequeStatuses(prev => ({
      ...prev,
      [itemId]: newStatus
    }));
  };

  // Get status color and text
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case 1:
        return { text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' };
      case 2:
        return { text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' };
      case 3:
        return { text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' };
      case 4:
        return { text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' };
      default:
        return { text: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
    }
  };

  // Open bulk cheque status update modal
  const handleOpenBulkChequeStatusModal = () => {
    console.log('=== OPENING BULK CHEQUE STATUS MODAL ===');

    const chequePaymentMethodId = getChequePaymentMethodId();
    console.log('Filtering items with PaymentType:', chequePaymentMethodId);
    console.log('Total filtered data items:', filteredData.length);

    const chequeItems = filteredData.filter(item => {
      const isPaymentTypeMatch = item.PaymentType === chequePaymentMethodId;
      const isActive = item.IsActive;
      const isStatusUpdateable = item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1;
      const isMatch = isPaymentTypeMatch && isActive && isStatusUpdateable;

      console.log(`Item ${item.Id} (${item.BillNumber}): PaymentType=${item.PaymentType}, IsActive=${item.IsActive}, ChequeStatusId=${item.ChequeStatusId}, Updateable=${isStatusUpdateable}, Match=${isMatch}`);
      return isMatch;
    });

    console.log('Found cheque items:', chequeItems.length);
    console.log('Cheque items details:', chequeItems);

    if (chequeItems.length === 0) {
      console.log('No updateable cheque items found - showing error');
      setError('No pending cheque payments found to update. Only cheques with Pending status can be updated.');
      return;
    }

    setSelectedChequeItems(chequeItems);
    setNewChequeStatus(2); // Default to "Passed"

    // Initialize individual statuses with default value
    const initialStatuses = {};
    chequeItems.forEach(item => {
      initialStatuses[item.Id] = item.ChequeStatusId || 1; // Use current status or default to "Pending"
    });
    setIndividualChequeStatuses(initialStatuses);

    console.log('Initial statuses:', initialStatuses);
    console.log('=== END MODAL OPENING DEBUG ===');

    setBulkChequeStatusModalOpen(true);
  };

  // Filter and search logic
  const filteredData = useMemo(() => paymentReceipts.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    const matchesType = filterType === 'all' ||
      (filterType === 'payment' && item.IsType) ||
      (filterType === 'receipt' && !item.IsType);

    return matchesSearch && matchesStatus && matchesType;
  }), [paymentReceipts, searchTerm, filterStatus, filterType]);

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = useMemo(
    () => filteredData.slice(startIndex, startIndex + rowsPerPage),
    [filteredData, startIndex, rowsPerPage]
  );

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPaymentTypeText = (paymentType) => {
    const paymentMethod = paymentMethods.find(pm => pm.Id === paymentType);
    return paymentMethod ? paymentMethod.Name : 'Cash';
  };

  // Handle expand row functionality - REMOVED (not needed)

  if (!mounted) return null;

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{ minWidth: '100%', background: '#fdf6ec', padding: '10px', position: 'relative' }}>
        {loading && <LogoLoader open={loading} message="Loading Payment & Receipts..." />}

        <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', color: 'black', fontFamily: 'Times New Romans' }}>
          {/* Header Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Romans', textTransform: 'uppercase' }}>
              <span style={{ color: 'black' }}>💳 Payment & Receipts</span>
            </h4>
          </div>

          {/* Date Filters, Search and Create Button Row */}
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
              <TextField
                type="date" size="small" value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
              <TextField
                type="date" size="small" value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{ width: '140px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '11px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
              />
            </div>
            <TextField
              fullWidth size="small" placeholder="Search by voucher, party or remarks..." value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: '#c9a84c', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '200px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#fdf6ec', fontSize: '13px', height: '29px', '&:hover fieldset': { borderColor: '#c9a84c' }, '&.Mui-focused fieldset': { borderColor: '#c9a84c' } } }}
            />
            <Tooltip title="Create a new payment/receipt" arrow>
              {canCreate && (
              <Button variant="contained" onClick={() => handleOpenModal('create')}
                style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', border: 'none', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap', boxShadow: '0 2px 8px rgba(92,61,30,0.4)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <span style={{ fontSize: '14px' }}>+</span> Create
              </Button>
              )}
            </Tooltip>
          </div>

          {/* Table */}
          <div style={{ marginBottom: '10px' }}>
            <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
              <Table sx={{ minWidth: 650 }} aria-label="payment receipts table">
                <TableHead>
                  <TableRow style={{ background: 'linear-gradient(135deg, #5c3d1e 0%, #5c3d1e 100%)', height: '27px' }}>
                    {['Voucher No', 'Date', 'Type', 'Party', 'Payment Type', 'Amount', 'Status', 'Actions'].map((h, i) => (
                      <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 7 ? 'right' : 'left' }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} style={{ textAlign: 'center', padding: '20px', color: '#999', fontSize: '13px' }}>
                        No payment receipts found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((item, index) => (
                      <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#fdf6ec', borderBottom: '2px solid #f0f0f0' }}>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '600' }}>
                          {item.BillNo}
                          <div style={{ fontSize: '10px', color: '#999', fontWeight: '400' }}>FY: {item.FinYear}</div>
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                          {formatDate(item.BillDate)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                          <Chip
                            label={item.IsType ? 'Payment' : 'Receipt'}
                            size="small"
                            style={{ fontSize: '10px', height: '18px', background: item.IsType ? '#fee2e2' : '#d1fae5', color: item.IsType ? '#dc2626' : '#065f46', fontWeight: '600' }}
                          />
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: '500' }}>
                          {item.MasterName || 'N/A'}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                          {getPaymentTypeText(item.PaymentType)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2e7d32', fontWeight: '600' }}>
                          {formatCurrency(item.Amount)}
                        </TableCell>
                        <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '600', padding: '1px 6px', borderRadius: '4px', background: item.IsActive ? '#e8f5e9' : '#fce4ec', color: item.IsActive ? '#2e7d32' : '#c62828' }}>
                            {item.IsActive ? 'Active' : 'Inactive'}
                          </span>
                        </TableCell>
                        <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Tooltip title="View Details" arrow>
                              <Button size="small" onClick={() => handleOpenModal('view', item)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(102,126,234,0.3)' }}>
                                👁️
                              </Button>
                            </Tooltip>
                            <Tooltip title="Print / Download PDF" arrow>
                              <Button size="small" onClick={() => handlePrintPaymentReceipt(item.Id)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(17,153,142,0.3)' }}>
                                🖨️
                              </Button>
                            </Tooltip>
                            {canEdit && (
                            <Tooltip title="Edit" arrow>
                              <Button size="small" onClick={() => handleOpenModal('edit', item)}
                                style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: '0 1px 4px rgba(245,158,11,0.3)' }}>
                                ✏️
                              </Button>
                            </Tooltip>
                            )}
                            {canDelete && (
                              <Tooltip title={item.IsActive ? 'Delete' : 'Activate'} arrow>
                                <Button size="small"
                                  onClick={() => {
                                    if (item.IsActive) { setDeleteItemId(item.Id); setDeleteItemNo(item.BillNumber); setDeleteModalOpen(true); }
                                    else { setActivateItemId(item.Id); setActivateItemNo(item.BillNumber); setActivateModalOpen(true); }
                                  }}
                                  style={{ minWidth: '22px', height: '22px', borderRadius: '6px', color: 'white', padding: '2px', border: 'none', fontSize: '10px', fontWeight: '600', boxShadow: item.IsActive ? '0 1px 4px rgba(255,107,107,0.3)' : '0 1px 4px rgba(78,205,196,0.3)' }}>
                                  {item.IsActive ? '🗑️' : '✅'}
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={filteredData.length}
            itemsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newSize) => {
              setRowsPerPage(newSize);
              setCurrentPage(1);
            }}
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Delete Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to delete voucher {deleteItemNo}? This action cannot be undone.</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setDeleteModalOpen(false)} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)} PaperProps={{ style: { borderRadius: '12px', padding: '8px' } }}>
          <DialogTitle style={{ fontSize: '16px', fontWeight: '600', color: '#2d3748' }}>Activate Record?</DialogTitle>
          <DialogContent>
            <DialogContentText style={{ fontSize: '13px', color: '#666' }}>Are you sure you want to activate voucher {activateItemNo}?</DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button onClick={() => setActivateModalOpen(false)} style={{ background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>❌</span> Cancel
            </Button>
            <Button onClick={handleActivateConfirm} style={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#ffffff', borderRadius: '6px', padding: '4px 8px', fontSize: '12px', fontWeight: '600', textTransform: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', minHeight: '26px' }}>
              <span>✅</span> Confirm Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Cheque Status Update Modal */}
        <Dialog
          open={bulkChequeStatusModalOpen}
          onClose={() => {
            setBulkChequeStatusModalOpen(false);
            setIndividualChequeStatuses({});
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle sx={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Payment />
              Update Cheque Status
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
              Update the status of pending cheque payments below:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {selectedChequeItems.map((item) => (
                <Box
                  key={item.Id}
                  sx={{
                    p: 2,
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {item.BillNumber} - {item.MasterName}
                    </Typography>
                    <Chip
                      label={formatCurrency(item.Amount)}
                      size="small"
                      sx={{ backgroundColor: '#dbeafe', color: '#1e40af' }}
                    />
                  </Box>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={individualChequeStatuses[item.Id] || 1}
                      onChange={(e) => setIndividualChequeStatuses(prev => ({
                        ...prev,
                        [item.Id]: parseInt(e.target.value)
                      }))}
                      label="Status"
                    >
                      <MenuItem value={1}>Pending</MenuItem>
                      <MenuItem value={2}>Passed</MenuItem>
                      <MenuItem value={3}>Bounced</MenuItem>
                      <MenuItem value={4}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => {
                setBulkChequeStatusModalOpen(false);
                setIndividualChequeStatuses({});
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkUpdateChequeStatus}
              variant="contained"
              sx={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
                }
              }}
            >
              Update All
            </Button>
          </DialogActions>
        </Dialog>

        {/* Payment/Receipt Modal */}
        <PaymentReceiptsModal
          open={openModal}
          onClose={handleCloseModal}
          onSave={handleSave}
          modalAction={modalAction}
          initialData={initialData}
          dropdownData={dropdownData}
          banks={banks}
          paymentMethods={paymentMethods}
          saving={modalLoading}
          Companyid={companyId}
          token={token}
          fetchMaxBillNo={fetchMaxBillNo}
          onPrint={handlePrintPaymentReceipt}
        />

        {/* Success/Error Snackbar */}
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setSuccess('')} severity="success" sx={{ width: '100%' }}>
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={3000}
          onClose={() => setError('')}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      </div>
    </Fade>
  );
};

export default PaymentReceiptsIndex;
